import { CI_SRC, LOUNGE_SRC } from '@/lib/brand';

/**
 * 공식 CI 그림에서 <b>기준선이 밑변보다 얼마나 위인지</b>. 그림 높이에 대한 비율이다.
 *
 * `bomi-ci.png` 는 179px 상자에 글자가 앉아 있고, `B`·`M`·`I` 의 마지막 잉크 줄은 166 인데
 * <b>그 줄이 절반만 차 있다</b> — 실제 기준선은 166.5 다. 12 로 잡으면 그림이 반 픽셀 뜬다.
 *
 * 눈으로 더 맞추고 싶으면 이 숫자 하나만 만진다. 키우면 그림이 내려가고, 줄이면 올라간다.
 */
const CI_BASELINE_GAP = 12.5 / 179;

/** CI 그림에서 <b>대문자 높이 ÷ 그림 높이</b>. `bomi-ci.png` 의 `B` 가 179px 상자에 153px 로 앉아 있다. */
const CI_CAP_RATIO = 153 / 179;

/**
 * `Lounge` 그림에서 <b>대문자 높이 ÷ 그림 높이</b>. 나머지 0.256 은 `g` 가 기준선 아래로 내려간 몫이다.
 * 잘라 낸 그림에서 `L`·`u`·`n` 의 발을 찾아 실측했다.
 */
const LOUNGE_BASELINE = 0.744;

/**
 * `Lounge` 의 대문자를 `BoMI` 대문자의 몇 배로 세울지 (사용자가 눈으로 맞춘 값 · 2026-09-11).
 *
 * <b>크게·작게는 여기서 한다.</b> 폭을 px 로 박으면 그 높이에서만 맞고 다른 자리에서는 찌그러진다 —
 * 0.820 은 헤더(22px)에서 폭 65px 이 나오는 값이고, 모바일·로그인도 같은 비율로 따라온다.
 */
const LOUNGE_CAP_RATIO = 0.75;

/**
 * `Lounge` 그림 높이 ÷ CI 높이.
 *
 * 숫자를 직접 적지 않고 위 셋에서 뽑는다 — 두 그림은 상자 안에서 글자가 차지하는 몫이 서로 달라,
 * 같은 높이를 주면 `BoMI` 와 `Lounge` 의 대문자 크기가 어긋난다. 그림을 갈아 끼울 때
 * {@link LOUNGE_BASELINE} 만 다시 재면 보이는 크기는 그대로 남는다.
 */
const LOUNGE_HEIGHT = (CI_CAP_RATIO * LOUNGE_CAP_RATIO) / LOUNGE_BASELINE;

/** `Lounge` 그림의 가로세로(553÷170). 폭을 손으로 계산하려면 필요하다. */
const LOUNGE_ASPECT = 553 / 170;

/**
 * `Lounge` 를 <b>가로로만</b> 늘리는 배. 1 이면 그림 원본 비율이고, 높이는 건드리지 않는다.
 *
 * 그림 속 `o` 는 가로세로 <b>0.914 인 세로 타원</b>이다(256×280 실측). 배율로는 모양이 안 바뀌니
 * 여기서 잡는다 — <b>1.094 면 정원(1.000)</b>, <b>1.269 면 공식 CI 의 `o`(1.160)</b> 와 같아진다.
 *
 * 늘린 만큼 `L`·`u`·`n`·`g`·`e` 도 함께 넓어진다. `o` 하나만 고칠 방법은 없다.
 */
const LOUNGE_STRETCH = 1.094;

/**
 * BoMI Lounge 표식 — 헤더 홈 버튼·로그인 카드가 함께 쓴다 (DOC-51 §6).
 *
 * <b>그림 두 조각 + 글자 한 조각</b>이다. `BoMI` 는 공식 CI(`bomi-ci.png`), `Lounge` 는
 * {@link LOUNGE_SRC}(`loungeCI_3.png` 에서 구웠다) 다. 둘 다 늘리지 않는다.
 *
 * 두 그림은 상자 안에서 글자가 앉은 자리가 서로 다르다 — CI 는 밑에 여백이 남고, `Lounge` 는
 * `g` 의 내림이 아래를 채운다. 그래서 <b>같은 `height` 를 주면 발도 크기도 어긋난다.</b>
 * 크기는 {@link LOUNGE_HEIGHT}, 발은 {@link CI_BASELINE_GAP}·{@link LOUNGE_BASELINE} 이 맞춘다.
 * 둘 다 그림에서 잰 값이라 그림을 갈아 끼우면 다시 재야 한다.
 *
 * 뒤에 붙는 `그룹웨어` 만 곁들이는 글자라 좁은 화면에서는 뺀다.
 */
export function BrandMark({
  height = 22,
  wordmark = false,
}: {
  /** CI 이미지 높이(px). 폭은 원본 비율(608:179)대로 따라온다 — 늘리지 않는다. */
  height?: number;
  /** 로고 옆에 '그룹웨어' 를 붙일지. */
  wordmark?: boolean;
}) {
  return (
    /*
      셋을 <b>기준선</b>에 세운다. 밑변에 맞추면 `Lounge` 의 `g` 가 기준선 아래로 내려가는 만큼
      글자 전체가 떠올라 CI 보다 높이 앉는다.

      `<img>` 의 기준선은 그림의 밑변이라, 그림 속 글자의 발보다 CI_BASELINE_GAP 만큼 아래다.
      그래서 그림만 그만큼 내려 앉힌다 — 이러면 `BoMI` 의 발과 `Lounge` 의 발이 한 줄에 선다.
    */
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: `${height * 0.10}px`, minWidth: 0 }}>
      <img
        src={CI_SRC}
        alt="BoMI"
        height={height}
        style={{
          height: `${height}px`,
          width: 'auto',
          display: 'block',
          position: 'relative',
          top: `${height * CI_BASELINE_GAP}px`,
        }}
      />
      {/*
        `Lounge` 도 그림이다. `<img>` 의 기준선은 밑변인데 글자의 발은 그보다 (1-0.744) 만큼
        <b>위</b>에 있다 — 그 아래는 `g` 의 내림이 채운다. 그래서 발을 기준선까지 <b>내려</b>
        앉힌다. CI 와 방향이 같다(둘 다 양수).

        폭은 px 로 박지 않는다 — 그 높이에서만 맞고 다른 자리에서는 찌그러진다(59px 은 로그인
        카드에서 그림을 절반 폭으로 눌렀다). <b>높이에서 비례로 뽑고 LOUNGE_STRETCH 만 곱한다</b> —
        세 자리가 같은 모양으로 따라오면서 `o` 는 여기서 잡힌다.
      */}
      <img
        src={LOUNGE_SRC}
        alt="Lounge"
        style={{
          height: `${height * LOUNGE_HEIGHT}px`,
          width: `${height * LOUNGE_HEIGHT * LOUNGE_ASPECT * LOUNGE_STRETCH}px`,
          display: 'block',
          position: 'relative',
          top: `${height * LOUNGE_HEIGHT * (1 - LOUNGE_BASELINE)}px`,
        }}
      />
      {wordmark && (
        <span
          style={{
            fontSize: `${Math.round(height * 0.42)}px`,
            fontWeight: 500,
            lineHeight: 1,
            color: 'var(--text-muted)',
            whiteSpace: 'nowrap',
            // `Lounge` 와는 조금 더 붙여 둔다 — 상표와 설명이 같은 간격이면 세 낱말로 읽힌다.
            marginLeft: `${height * 0.10}px`,
          }}
        >
          그룹웨어
        </span>
      )}
    </span>
  );
}
